/**
 * inputComponent — viewport user input as a blueprint-visible source (camera-in-blueprint
 * Phase 1). Modeled on movementComponent: module singleton, state lives HERE, not in React
 * (CLAUDE.md hard rule — this is read on the render hot path).
 *
 * Ownership: this component owns viewport pointer/key input. It is canvas-scoped — listeners
 * attach to the passed element, never to window, except keys (which have no element target
 * without a tabindex) and those are gated on the canvas being hovered or focused. That gating
 * is the fix for App.jsx's old `e.target.matches('input, textarea, select')` guard, which let
 * a key pressed over the *blueprint* canvas fire graph chains.
 *
 * Dispatch cadence — discrete vs coalesced:
 *   - onPointerDown / onPointerUp / onKeyDown / onKeyUp  fire IMMEDIATELY (inherently rare).
 *   - onPointerDrag / onWheel                            fire ONCE PER FRAME from
 *     consumeFrame(), with deltas accumulated since the last frame.
 *
 * Coalescing is a dispatch cadence, NOT a change to the authoring model: a graph wiring
 * [On Pointer Drag] --out-dx--> [Camera Add Yaw] behaves identically either way. The reason
 * is not taming a firehose — Chrome and Firefox already align `pointermove` dispatch to the
 * animation frame (that's why getCoalescedEvents() exists; only `pointerrawupdate` runs at
 * device rate). The real reasons are (a) determinism: input integrates at a fixed point in
 * the frame relative to useFrame, matching the movementComponent house pattern; (b) `wheel`
 * is NOT rAF-aligned and genuinely needs coalescing; (c) it caps graph exec at frame rate
 * regardless of future browser behaviour.
 *
 * Coalescing is only SEMANTICALLY equivalent to per-event firing given three rules, all
 * implemented below:
 *   1. Flush before transitions — a down→drag→up inside one frame would otherwise dispatch
 *      the drag AFTER the release, silently breaking any graph that gates motion on
 *      isButtonHeld / an onPointerUp "idle" transition.
 *   2. Per-button accumulators — onPointerDrag carries a button filter, so a mid-frame
 *      button change must not misattribute deltas to the wrong stream.
 *   3. A defined modifier snapshot on the coalesced payload (last-wins, see _Accum).
 *
 * Hold semantics need no repeat machinery: isButtonHeld/isKeyHeld/getAxis are DATA reads of
 * this singleton, so a graph asks "is it held" rather than being told 60×/s.
 *
 * DOM-free by injection so it unit-tests without jsdom (this repo runs bare `node --test`):
 * attach() takes the element and window, and only ever touches addEventListener /
 * removeEventListener / setPointerCapture, all feature-detected.
 */

// Pointer `button` (down/up) → our name. NOT the same encoding as `buttons`.
const BUTTON_NAME = ['left', 'middle', 'right']
// Pointer `buttons` bitmask → our name. left=1, right=2, middle=4.
const BUTTON_MASK = { left: 1, right: 2, middle: 4 }
const ALL_BUTTONS = ['left', 'middle', 'right']

// wheel deltaMode → pixels. 0 = already pixels; 1 = lines; 2 = pages.
const LINE_HEIGHT_PX = 16
const PAGE_HEIGHT_PX = 800

function isEditableTarget(t) {
  if (!t) return false
  if (typeof t.matches === 'function' && t.matches('input, textarea, select')) return true
  return !!t.isContentEditable
}

/**
 * Per-button coalescing accumulator. One instance per button, so its payload object is
 * stable and reused frame to frame — this REDUCES (does not eliminate) per-frame allocation
 * on this path (CLAUDE.md); fireEvent still builds a `ev.${type}` string + a tracePhase
 * closure per fire.
 *
 * The payload is handed to fireEvent, which stashes it in _lastEventData[type] and
 * debugState.eventData[type] — both of which OUTLIVE the synchronous exec chain. Be precise
 * about what that means:
 *   - dx/dy only change at take(), i.e. at the next dispatch.
 *   - x/y and the modifier flags are rewritten by add() on EVERY pointermove, so the stashed
 *     reference mutates BETWEEN fires, not just at them.
 * So a consumer that resolves data pins synchronously (the normal case) is exact, while a
 * Delay'd chain resuming later reads current-ish values. That is the same staleness class as
 * before this change — a fresh object per fire was still overwritten in the stash by
 * reference, so a Delay never saw fire-time data either; reuse only narrows the window from
 * "last fire" to "last DOM event". It stays within the runtime's documented
 * "last payload for this type wins" model.
 *
 * Aliasing note: with two buttons held, each _Accum has its OWN payload, so
 * _lastEventData['onPointerDrag'] points at whichever button fired last. A Delay'd left-drag
 * chain can therefore resume reading the middle button's payload. "Last wins" again, but it
 * is worth knowing before debugging it.
 */
class _Accum {
  constructor(button) {
    this.button = button
    this.dx = 0
    this.dy = 0
    this.moved = false
    // Reused payload. `modifiers` is last-wins over the coalesced window.
    this.payload = { button, dx: 0, dy: 0, x: 0, y: 0, shift: false, ctrl: false, alt: false, meta: false }
  }
  add(dx, dy, x, y, mods) {
    this.dx += dx
    this.dy += dy
    this.moved = true
    const p = this.payload
    p.x = x; p.y = y
    p.shift = mods.shift; p.ctrl = mods.ctrl; p.alt = mods.alt; p.meta = mods.meta
  }
  /** Fill + return the payload for dispatch, then zero the deltas. */
  take() {
    const p = this.payload
    p.dx = this.dx; p.dy = this.dy
    this.dx = 0; this.dy = 0; this.moved = false
    return p
  }
  zero() { this.dx = 0; this.dy = 0; this.moved = false }
}

class InputComponent {
  constructor() {
    this._el = null
    this._win = null
    this._eventSink = null
    this._handlers = null

    this.cfg = {
      wheelScale: 1,        // multiplier applied after deltaMode normalization
      keysNeedHover: true,  // false = keys fire whenever the app has focus (not canvas-scoped)
    }

    this.reset()
  }

  reset() {
    this.buttons = 0                       // live `buttons` bitmask
    this.modifiers = { shift: false, ctrl: false, alt: false, meta: false }
    this.keys = new Set()                  // lowercased held keys
    this.pointers = new Map()              // pointerId → {x,y} — multi-touch (Phase 6)
    this.x = 0
    this.y = 0
    this.hovered = false
    this.focused = false

    this._accum = new Map()                // button → _Accum
    this._rightDragPx = 0                  // right-button click-vs-drag odometer (_onContextMenu)
    this._suppressNextContextMenu = false  // one-shot: right-DRAG released, eat the menu it spawns
    for (const b of ALL_BUTTONS) this._accum.set(b, new _Accum(b))
    // RAW per-frame motion, accumulated ONCE per move regardless of how many buttons are
    // held. getAxis('mouseX'/'mouseY') must read this, NOT the sum of the per-button
    // accumulators: Rule 2 deliberately attributes the same physical motion to every held
    // button, so summing them would report 2× motion when two buttons are chorded and whip
    // a `Get Input Axis → Camera Add Yaw` graph at double speed.
    this._rawDx = 0
    this._rawDy = 0
    this._wheel = 0
    this._wheelPayload = { delta: 0, shift: false, ctrl: false, alt: false, meta: false }
    this._lastX = 0
    this._lastY = 0
  }

  // --- wiring ---
  setEventSink(fn) { this._eventSink = fn }   // (type, data) => void
  configure(patch) { Object.assign(this.cfg, patch) }

  /**
   * Attach canvas-scoped listeners. `el` is the WebGL canvas (gl.domElement); `win` is
   * injectable so unit tests can pass a fake. Idempotent — re-attaching detaches first.
   */
  attach(el, win = (typeof window !== 'undefined' ? window : null)) {
    if (!el || typeof el.addEventListener !== 'function') return false
    if (this._el) this.detach()
    this._el = el
    this._win = win

    const h = {
      pointerdown: e => this._onPointerDown(e),
      pointermove: e => this._onPointerMove(e),
      pointerup: e => this._onPointerUp(e),
      pointercancel: e => this._onPointerCancel(e),
      pointerenter: () => { this.hovered = true },
      pointerleave: () => { this.hovered = false },
      wheel: e => this._onWheel(e),
      contextmenu: e => this._onContextMenu(e),
      focus: () => { this.focused = true },
      blur: () => { this.focused = false; this._releaseAll() },
    }
    for (const [type, fn] of Object.entries(h)) {
      // wheel must be non-passive: it preventDefault()s to stop page scroll.
      el.addEventListener(type, fn, type === 'wheel' ? { passive: false } : undefined)
    }

    // Keys have no element target without a tabindex on the canvas, so they listen on the
    // window and are gated by _canTakeKeys(). window blur releases everything held — a key
    // or button "stuck down" because focus left mid-drag would drive the camera forever.
    const wh = {
      keydown: e => this._onKeyDown(e),
      keyup: e => this._onKeyUp(e),
      blur: () => this._releaseAll(),
    }
    if (win && typeof win.addEventListener === 'function') {
      for (const [type, fn] of Object.entries(wh)) win.addEventListener(type, fn)
    }

    this._handlers = { el: h, win: wh }
    return true
  }

  detach() {
    if (!this._el || !this._handlers) return
    for (const [type, fn] of Object.entries(this._handlers.el)) {
      this._el.removeEventListener?.(type, fn)
    }
    if (this._win && typeof this._win.removeEventListener === 'function') {
      for (const [type, fn] of Object.entries(this._handlers.win)) {
        this._win.removeEventListener(type, fn)
      }
    }
    this._el = null
    this._win = null
    this._handlers = null
    this.reset()
  }

  // --- data reads (Is Button Held / Is Key Held / Get Input Axis nodes) ---
  isButtonHeld(button) {
    if (button === 'any') return this.buttons !== 0
    const m = BUTTON_MASK[button]
    return m ? (this.buttons & m) !== 0 : false
  }

  isKeyHeld(key) {
    return typeof key === 'string' && this.keys.has(normalizeKey(key))
  }

  /**
   * Live axis value. mouseX/mouseY are the deltas accumulated SO FAR this frame (they zero
   * on consumeFrame), so a data-pin read inside a drag chain sees the same value the event
   * payload carries. pointerX/pointerY are absolute canvas coords.
   */
  getAxis(axis) {
    switch (axis) {
      // Raw, NOT the sum of per-button accumulators — see _rawDx.
      case 'mouseX': return this._rawDx
      case 'mouseY': return this._rawDy
      case 'wheel': return this._wheel
      case 'pointerX': return this.x
      case 'pointerY': return this.y
      default: return 0
    }
  }

  // --- per-frame ---
  /**
   * Dispatch the coalesced events for this frame. Called once per frame by <ViewportInput>.
   * Fires onPointerDrag per moved button, then onWheel. Zeroes accumulators.
   */
  consumeFrame() {
    for (const b of ALL_BUTTONS) {
      const a = this._accum.get(b)
      if (a.moved) this._fire('onPointerDrag', a.take())
    }
    this._rawDx = 0
    this._rawDy = 0
    if (this._wheel !== 0) {
      const p = this._wheelPayload
      p.delta = this._wheel
      p.shift = this.modifiers.shift; p.ctrl = this.modifiers.ctrl
      p.alt = this.modifiers.alt; p.meta = this.modifiers.meta
      this._wheel = 0
      this._fire('onWheel', p)
    }
  }

  /**
   * Rule 1: flush a button's pending drag NOW, before a down/up transition for that button
   * is dispatched. Without this, a down→drag→up inside one frame dispatches the drag after
   * the release and any graph gating motion on isButtonHeld silently drops it.
   */
  flushFor(button) {
    const a = this._accum.get(button)
    if (a?.moved) this._fire('onPointerDrag', a.take())
  }

  // --- DOM handlers ---
  /**
   * Native context menu: a right-DRAG (camera pan) suppresses it; a stationary
   * right-click does not.
   *
   * This used to be an unconditional preventDefault, which was correct while
   * ViewportInput mounted only in live mode — but Wave 2B mounts it always, so
   * the blanket rule made the canvas menu unreachable app-wide and broke the
   * waypoint picker's documented contract ("disarmed: the picker leaves the
   * canvas menu alone" — its armed overlay installs its OWN preventDefault, and
   * the arm/disarm difference was unobservable with this one always on).
   *
   * Movement-gated, not held-gated, because contextmenu TIMING is per-platform:
   * Windows fires it after pointerup (right button no longer held), Linux at
   * pointerdown (held, zero movement yet). So: suppress while a right-drag has
   * accumulated real movement (mid-drag firing), or when the just-released
   * right button had (post-release firing, `_suppressNextContextMenu`). The 3px
   * threshold is the usual click-vs-drag slop. The one-shot flag is cleared on
   * the next pointerdown too, so a suppressed-but-never-fired menu (platform
   * without the post-up event) can't eat an unrelated menu later.
   */
  _onContextMenu(e) {
    const rightDragging = (this.buttons & BUTTON_MASK.right) !== 0 && this._rightDragPx > 3
    if (rightDragging || this._suppressNextContextMenu) {
      this._suppressNextContextMenu = false
      e.preventDefault?.()
    }
  }

  _onPointerDown(e) {
    if (this._ignorePointer(e)) return
    this._suppressNextContextMenu = false
    if (e.button === 2) this._rightDragPx = 0
    const name = BUTTON_NAME[e.button]
    // A second button pressed mid-drag is a transition for the buttons already down: their
    // accumulated motion belongs to the pre-transition state. Note the asymmetry with
    // _onPointerUp, which flushes ONLY the released button: a graph gating middle-drag on
    // isButtonHeld('left') therefore sees left's release before middle's pending drag.
    // Defensible (a down changes the meaning of every in-flight stream; an up doesn't), but
    // it is an asymmetry, not an oversight.
    for (const b of ALL_BUTTONS) if (this.isButtonHeld(b)) this.flushFor(b)

    this.buttons = e.buttons ?? (name ? BUTTON_MASK[name] : 0)
    this.hovered = true   // see _markHovered
    this._syncModifiers(e)
    this._updatePos(e)
    this.pointers.set(e.pointerId, { x: this.x, y: this.y })
    // Capture so a drag that leaves the canvas keeps delivering — matches OrbitControls.
    try { this._el?.setPointerCapture?.(e.pointerId) } catch { /* not supported — ignore */ }
    if (!name) return
    this._fire('onPointerDown', {
      button: name, x: this.x, y: this.y, ...this.modifiers,
    })
  }

  _onPointerMove(e) {
    if (this._ignorePointer(e)) return
    this.hovered = true   // see _markHovered
    this._syncModifiers(e)
    this._updatePos(e)   // leaves _lastX/_lastY = the position before this move
    if (this.pointers.has(e.pointerId)) this.pointers.set(e.pointerId, { x: this.x, y: this.y })
    if (this.buttons === 0) return
    const dx = this.x - this._lastX
    const dy = this.y - this._lastY
    if (dx === 0 && dy === 0) return
    this._rawDx += dx
    this._rawDy += dy
    // Click-vs-drag odometer for the right button (see _onContextMenu).
    if ((this.buttons & BUTTON_MASK.right) !== 0) this._rightDragPx += Math.abs(dx) + Math.abs(dy)
    // Rule 2: attribute to every button currently held, so each drag stream sees the motion
    // and a filter on one button can't be starved by another being down.
    for (const b of ALL_BUTTONS) {
      if (this.isButtonHeld(b)) this._accum.get(b).add(dx, dy, this.x, this.y, this.modifiers)
    }
  }

  _onPointerUp(e) {
    if (this._ignorePointer(e)) return
    const name = BUTTON_NAME[e.button]
    this._syncModifiers(e)
    this._updatePos(e)
    // Windows fires contextmenu AFTER this up — arm the one-shot when the
    // released right button was a drag, not a click (see _onContextMenu).
    if (e.button === 2) {
      this._suppressNextContextMenu = this._rightDragPx > 3
      this._rightDragPx = 0
    }
    // Rule 1 — flush before the release is dispatched.
    if (name) this.flushFor(name)
    this.buttons = e.buttons ?? 0
    // Release capture only once NOTHING is held. Releasing on any button-up would kill a
    // still-held left-drag the moment the pointer left the canvas, just because the right
    // button was tapped and released mid-drag.
    if (this.buttons === 0) {
      this.pointers.delete(e.pointerId)
      try { this._el?.releasePointerCapture?.(e.pointerId) } catch { /* ignore */ }
    }
    if (!name) return
    this._fire('onPointerUp', {
      button: name, x: this.x, y: this.y, ...this.modifiers,
    })
  }

  /**
   * pointercancel — the browser took the pointer away (touch scroll takeover, OS gesture).
   * It CANNOT route through _onPointerUp: per the Pointer Events spec a cancel carries
   * `button === -1` (no button-state change), so BUTTON_NAME[-1] is undefined and the whole
   * release path would be skipped — leaving a pending accumulator to dispatch an
   * onPointerDrag AFTER the cancel (violating Rule 1 in the one scenario cancels exist for)
   * and leaving any down/up latch in the graph stuck on.
   *
   * So: flush every held button (Rule 1), then dispatch a synthetic release for each, marked
   * `canceled` so a graph can tell an abort from a real release.
   */
  _onPointerCancel(e) {
    this._syncModifiers(e)
    this._updatePos(e)
    const held = ALL_BUTTONS.filter(b => this.isButtonHeld(b))
    for (const b of held) this.flushFor(b)
    this.buttons = 0
    this.pointers.delete(e.pointerId)
    try { this._el?.releasePointerCapture?.(e.pointerId) } catch { /* ignore */ }
    for (const b of held) {
      this._fire('onPointerUp', {
        button: b, x: this.x, y: this.y, ...this.modifiers, canceled: true,
      })
    }
  }

  _onWheel(e) {
    // No gate: this listener is canvas-scoped, so a wheel event ARRIVING is itself proof the
    // pointer is over the viewport. The old `!_canTakeKeys() && !hovered` gate could only
    // ever false-block (it conflated a keyboard concept with wheel behaviour, and dropped
    // the first wheel — page-scrolling instead — whenever the canvas was attached with the
    // cursor already parked over it).
    this.hovered = true
    e.preventDefault?.()
    this._syncModifiers(e)
    let d = e.deltaY ?? 0
    if (e.deltaMode === 1) d *= LINE_HEIGHT_PX
    else if (e.deltaMode === 2) d *= PAGE_HEIGHT_PX
    this._wheel += d * this.cfg.wheelScale
  }

  _onKeyDown(e) {
    if (!this._canTakeKeys()) return
    if (isEditableTarget(e.target)) return
    this._syncModifiers(e)
    const k = normalizeKey(e.key)
    const repeat = this.keys.has(k)
    this.keys.add(k)
    // OS auto-repeat must not re-fire the chain — hold is a data read, not a repeat.
    if (repeat || e.repeat) return
    this._fire('onKeyDown', { key: k, ...this.modifiers })
  }

  _onKeyUp(e) {
    this._syncModifiers(e)
    const k = normalizeKey(e.key)
    // The delete() gate IS the legitimacy check: a keydown that went to an input was never
    // tracked, so its keyup returns right here. Re-checking isEditableTarget on the UP would
    // be actively harmful — hold W over the viewport, click into the chat box, release W,
    // and the graph would never get the release while its down/up latch stayed stuck on.
    if (!this.keys.delete(k)) return
    this._fire('onKeyUp', { key: k, ...this.modifiers })
  }

  // --- internals ---
  /**
   * Multi-touch is Phase 6. Until then, ignore non-primary pointers outright: every touch
   * contact arrives as button 0 / buttons 1, and x/y/_lastX/_lastY are single-pointer, so
   * two interleaved contacts would each measure dx from the OTHER finger's position and the
   * camera would convulse by the full finger separation. Ignoring them makes a second finger
   * inert (merely unsupported) rather than destructive. `isPrimary` is undefined in the unit
   * tests' fakes, so only an explicit false is rejected.
   */
  _ignorePointer(e) {
    return e.isPrimary === false
  }

  /** Keys are canvas-scoped: only when the viewport is hovered or focused. */
  _canTakeKeys() {
    if (!this.cfg.keysNeedHover) return true
    return this.hovered || this.focused
  }

  _syncModifiers(e) {
    const m = this.modifiers
    m.shift = !!e.shiftKey; m.ctrl = !!e.ctrlKey; m.alt = !!e.altKey; m.meta = !!e.metaKey
  }

  /** Canvas-local coords, so a resized/offset canvas doesn't skew deltas. */
  _updatePos(e) {
    this._lastX = this.x
    this._lastY = this.y
    const r = this._el?.getBoundingClientRect?.()
    if (r) {
      this.x = (e.clientX ?? 0) - r.left
      this.y = (e.clientY ?? 0) - r.top
    } else {
      this.x = e.clientX ?? 0
      this.y = e.clientY ?? 0
    }
  }

  /** Drop everything held (focus loss). Prevents a stuck button driving the camera forever. */
  _releaseAll() {
    this.buttons = 0
    this.keys.clear()
    this.pointers.clear()
    for (const a of this._accum.values()) a.zero()
    this._rawDx = 0
    this._rawDy = 0
    this._wheel = 0
  }

  _fire(type, data) {
    if (this._eventSink) { this._eventSink(type, data); return }
    const er = (typeof window !== 'undefined' && window.__EVENT_RUNTIME__) || null
    if (er && typeof er.fireEvent === 'function') er.fireEvent(type, data)
  }
}

/** ' ' → 'space' so a bound key round-trips; everything else lowercased. */
export function normalizeKey(key) {
  if (typeof key !== 'string') return ''
  if (key === ' ' || key === 'Spacebar') return 'space'
  return key.toLowerCase()
}

export const input = new InputComponent()

if (typeof window !== 'undefined') {
  window.__INPUT__ = {
    get state() {
      return {
        buttons: input.buttons,
        keys: [...input.keys],
        modifiers: { ...input.modifiers },
        x: input.x, y: input.y,
        hovered: input.hovered, focused: input.focused,
      }
    },
    isButtonHeld: b => input.isButtonHeld(b),
    isKeyHeld: k => input.isKeyHeld(k),
    getAxis: a => input.getAxis(a),
    _component: input,
  }
}
